"use client";
import * as Dialog from "@radix-ui/react-dialog";
import clsx from "clsx";
import Image from "next/image";
import { useParams, usePathname } from "next/navigation";
import { useState } from "react";

import type { FragmentType } from "../../../../__generated__/gql";
import { graphql, useFragment } from "../../../../__generated__/gql";
import type { ArchitectureTopNavigationQuery } from "../../../../__generated__/gql/graphql";
import { Link } from "../../../_components/ui/link";
import { useInNativeApp } from "../../../_hooks/use-in-native-app";

type NavigationData = ArchitectureTopNavigationQuery["architectureTopPage"];

export function ArchitectureLayoutShell({
  navigation,
  children,
}: {
  navigation: NavigationData;
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const currentNavigationItemName = useCurrentNavigationItemName(navigation);
  const pathname = usePathname();

  const isInNativeApp = useInNativeApp();

  return (
    <div>
      <div
        className={clsx(
          "flex",
          isInNativeApp === undefined
            ? ""
            : isInNativeApp
              ? "top-0 h-0"
              : "h-global-header-min sticky top-[60px] z-10 md:hidden",
        )}
      >
        <div className="hidden-in-nativeapp bg-slightbeige flex h-full w-full items-center">
          <Dialog.Root open={open} onOpenChange={setOpen}>
            <Dialog.Trigger asChild>
              <button
                className="font-prophet flex h-[40px] w-full items-center gap-[18px] pt-[5px] pl-[70px] text-[20px]"
                data-analytics-id="architecture_common_opened_menu"
              >
                <Image
                  src="/i/icon_chevron_down.svg"
                  className=""
                  width={16}
                  height={16}
                  alt="down"
                />
                {currentNavigationItemName}
              </button>
            </Dialog.Trigger>
            <Dialog.Portal>
              <Dialog.Overlay className="sd-overlay">
                <Dialog.Content
                  aria-describedby={undefined}
                  className="sd-content bg-slightbeige-116 w-[285px] p-[30px]"
                >
                  <Dialog.Title className="hidden">建築メニュー</Dialog.Title>
                  <Link
                    href="/architectures"
                    data-analytics-id="architecture_common_clicked_menu_top"
                    className={clsx("flex items-center whitespace-nowrap", {
                      "text-sorange": pathname === "/architectures",
                    })}
                    onClick={() => {
                      setOpen(false);
                    }}
                  >
                    Architecture TOP
                  </Link>
                  <div className="h-[20px]"></div>
                  <Navigation
                    navigation={navigation}
                    onClick={() => {
                      setOpen(false);
                    }}
                  />
                </Dialog.Content>
              </Dialog.Overlay>
            </Dialog.Portal>
          </Dialog.Root>
        </div>
      </div>
      <div className="bg-slightbeige text-skogecha relative w-full">
        <div className="hidden-in-nativeapp font-prophet text-skogecha fixed top-[40px] left-[10px] z-20 origin-bottom-left rotate-90 text-[30px] leading-none md:top-[70px] md:left-[40px] md:text-[45px]">
          <Link
            href="/architectures"
            data-analytics-id="architecture_common_clicked_side_top"
          >
            Architecture
          </Link>
        </div>
        <div
          className={clsx(
            "flex w-full",
            isInNativeApp
              ? "pt-0 pl-0"
              : "gap-[100px] pt-[60px] pl-[50px] md:pt-[180px] md:pl-0",
          )}
        >
          <div className="hidden-in-nativeapp hidden md:flex">
            <Navigation navigation={navigation} />
          </div>
          {children}
        </div>
      </div>
    </div>
  );
}

function useCurrentNavigationItemName(navigation: NavigationData) {
  const params = useParams<{ slug: string }>();
  const items = [
    ...navigation.originalSeries.flatMap((item) => [
      item,
      ...(item.subItems
        ? item.subItems.map((subItem) => ({
            ...subItem,
            name: `${item.name} ${subItem.name}`,
          }))
        : []),
    ]),
    ...navigation.selectionSeries,
    ...navigation.feature,
  ];
  const item = items.find((item) => item.slug === params.slug);
  return item ? item.name : "Original Series";
}

function Navigation({
  navigation,
  onClick,
}: {
  navigation: NavigationData;
  onClick?: () => void;
}) {
  return (
    <aside className="divide-sbeige font-prophet flex min-w-[181px] flex-col divide-y md:ml-[100px] lg:ml-[140px]">
      <NavigationSection title="Original Series">
        {navigation.originalSeries.map((item, i) => (
          <NavigationItem
            key={i}
            navigationItemFragment={item}
            onClick={onClick}
          />
        ))}
      </NavigationSection>
      <NavigationSection title="Selection Series">
        {navigation.selectionSeries.map((item, i) => (
          <NavigationItem
            key={i}
            navigationItemFragment={item}
            onClick={onClick}
          />
        ))}
      </NavigationSection>
      <NavigationSection title="Feature">
        {navigation.feature.map((item, i) => (
          <NavigationItem
            key={i}
            navigationItemFragment={item}
            onClick={onClick}
          />
        ))}
      </NavigationSection>
    </aside>
  );
}

function NavigationSection({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="not-first:pt-[30px]">
      <h2 className="text-[28px] whitespace-nowrap">{title}</h2>
      <div className="flex flex-col gap-[10px] pt-[20px] pb-[30px] text-[18px]">
        {children}
      </div>
    </div>
  );
}

const Fragment = graphql(`
  fragment ContentArchitecturePageNavigationItem on ContentArchitecturePageNavigationItem {
    name
    slug
    path
    area
    thumbnail {
      rawUrl
    }
    subItems {
      slug
      name
      path
    }
  }
`);

function NavigationItem({
  navigationItemFragment,
  onClick,
}: {
  navigationItemFragment: FragmentType<typeof Fragment>;
  onClick?: () => void;
}) {
  const params = useParams<{ slug: string }>();
  const item = useFragment(Fragment, navigationItemFragment);
  return (
    <div
      className={clsx("flex flex-col gap-[8px]", {
        "text-sorange": item.slug === params.slug,
      })}
    >
      {item.subItems && item.subItems.length > 0 ? (
        <>
          <div
            className={clsx({
              "text-sorange": item.subItems?.some(
                (subItem) => subItem.slug === params.slug,
              ),
            })}
          >
            {item.name}
          </div>
          <div className="border-sbeige ml-[5px] flex flex-col gap-[8px] border-l pl-[10px]">
            {item.subItems.map((subItem, j) => (
              <Link
                href={subItem.path}
                key={j}
                data-analytics-id="architecture_common_clicked_series"
                className={clsx({
                  "text-sorange": subItem.slug === params.slug,
                })}
                onClick={onClick}
              >
                {subItem.name}
              </Link>
            ))}
          </div>
        </>
      ) : (
        <Link
          href={item.path}
          className="flex items-center whitespace-nowrap"
          data-analytics-id="architecture_common_clicked_series"
          onClick={onClick}
        >
          {item.name}
          {item.area && (
            <span className="ml-[10px] text-[11px]">{item.area}</span>
          )}
        </Link>
      )}
    </div>
  );
}
